<template>
  <div class="substep-report-subscription">
    <div class="substep-header">
      <h3 data-testId="step-title">Subscribe to a Report</h3>
      <p>
        Pick a report that you'd find valuable to receive on a regular basis and we'll make sure it lands in your inbox
        with up-to-date data from your team.
      </p>
    </div>
    <div class="substep-body">
      <div
        v-for="(report, index) in reports"
        :key="report.id"
        class="report-card"
      >
        <div class="content">
          <div class="header">
            <div class="title">{{ report.title }}</div>
            <img
              v-if="report.existing_report_enabled"
              class="badge-check"
              :src="BADGE_CHECK"
            />
          </div>
          <div class="description">{{ report.description }}</div>
        </div>
        <div class="footer">
          <template v-if="report.existing_report_enabled">
            <AppButton
              :disabled="submitting[index]"
              modifiers="green-outline"
              @click="unsubscribe(report, index)"
            >
              Subscribed
              <i class="hi hi-check hi-12"></i>
            </AppButton>
          </template>
          <template v-else>
            <AppButton
              type="primary"
              :disabled="submitting[index]"
              @click="subscribe(report, index)"
            >
              Subscribe
            </AppButton>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GettingStartedMixin from '~/mixins/GettingStarted/GettingStartedMixin';
import AppButton from '~/components/AppButton.vue';
import { ScheduledReportsResource } from '~/resources';
import { BADGE_CHECK } from '~/rails_assets';

export default {
  name: 'SubscribeToAReport',
  components: {
    AppButton,
  },
  mixins: [GettingStartedMixin],
  props: {
    reports: {
      type: Array,
      default: () => [],
      validator: (value) => {
        const requiredAttributes = [
          'id',
          'title',
          'description',
          'existing_report_id',
          'existing_report_enabled',
          'new_report_attributes',
        ];
        const hasRequiredAttributes = (report) => {
          const reportKeys = Object.keys(report);

          return requiredAttributes.every((attr) => reportKeys.includes(attr));
        };

        return value.every((report) => hasRequiredAttributes(report));
      },
    },
    substepStatus: {
      type: String,
      default: 'current',
    },
  },
  data() {
    return {
      BADGE_CHECK,
      submitting: [],
    };
  },
  watch: {
    reports: {
      handler() {
        this.checkReports();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    checkReports() {
      const hasEnabledReport = this.reports.some((report) => report.existing_report_enabled);

      if (hasEnabledReport && this.substepStatus !== 'completed') {
        this.$emit('completeSubstep');
      }

      if (!hasEnabledReport && (this.substepStatus === 'completed' || this.substepStatus === 'current' )) {
        this.$emit('incompleteSubstep');
      }
    },
    async subscribe(report, index) {
      if (report.existing_report_id) {
        await this.changeSubscription(report, index, 'enable');
      } else {
        await this.createReport(report, index);
      }
    },
    async unsubscribe(report, index) {
      await this.changeSubscription(report, index, 'disable');
    },
    async changeSubscription(report, index, action) {
      try {
        this.submitting[index] = true;
        const response = await ScheduledReportsResource[action]({ id: report.existing_report_id });

        if (response.success) {
          this.$emit('reloadSubstep');
        } else if (response.errors) {
          this.$appMessage('Unexpected error. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      } finally {
        this.submitting[index] = false;
      }
    },
    async createReport(report, index) {
      try {
        this.submitting[index] = true;
        const response = await ScheduledReportsResource.create({}, report.new_report_attributes);

        if (response.success) {
          this.$emit('reloadSubstep');
        } else if (response.errors) {
          this.$appMessage('Unexpected error. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      } finally {
        this.submitting[index] = false;
      }
    },
  },
};
</script>
